import React from "react"
import dayjs from "dayjs"
import jsPDF from "jspdf"
import autoTable, { RowInput } from "jspdf-autotable"
import { renderToStaticMarkup } from "react-dom/server"
import { SvgZoeeGradientLogo } from "../../../../components/icons/logos/zoee-gradient-logo"

// ——— Helpers ————————————————————————————————————————————————————————————

function hexToRgbArray(hex: string): [number, number, number] {
  const m = hex.replace("#", "").match(/^([a-f0-9]{2})([a-f0-9]{2})([a-f0-9]{2})$/i)
  if (!m) return [0, 0, 0]
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)]
}

async function svgToPngBase64(svgMarkup: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image()
    const svg = new Blob([svgMarkup], { type: "image/svg+xml;charset=utf-8" })
    const url = URL.createObjectURL(svg)
    img.onload = () => {
      const canvas = document.createElement("canvas")
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext("2d")!
      ctx.drawImage(img, 0, 0)
      URL.revokeObjectURL(url)
      resolve(canvas.toDataURL("image/png"))
    }
    img.src = url
  })
}

const STATUS_COLORS: Record<string, { label: string; hex: string }> = {
  paid: { label: "Paid", hex: "#27A745" },            // green
  scheduled: { label: "Scheduled", hex: "#C79300" },  // gold
  overdue: { label: "Overdue", hex: "#D53C3C" },      // red
  refunded: { label: "Refunded", hex: "#3A7AFE" },    // blue
  denied: { label: "Denied", hex: "#6B7280" },        // gray
}

function formatMoney(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === "") return "—"
  const n = Number(v)
  if (Number.isNaN(n)) return "—"
  return `$${n.toFixed(2)}`
}

function formatPaymentMethod(method: any, amount: any): string {
  const hasAmount = amount !== null && amount !== undefined && Number(amount) > 0
  if (!hasAmount) return "—"
  if (!method) return "—"

  const m = String(method)
  const clean = m.replace(/\s+/g, "").toLowerCase()

  if (/^\d{4}$/.test(clean)) return `•••• ${m.slice(-4)}`
  if (/^\d{12,19}$/.test(clean)) return `•••• ${clean.slice(-4)}`
  if (clean.includes("paypal")) return "PayPal"
  if (m.includes("•") || /last4/i.test(m)) return m

  return m
}

function computeStatus(t: any): { label: string; hex: string } {
  const raw = String(t.payment_status || t.status || "").toLowerCase()
  const isOverdue =
    raw === "scheduled" && t.date_scheduled && dayjs(t.date_scheduled).isBefore(dayjs(), "day")

  if (isOverdue) return STATUS_COLORS["overdue"]
  if (raw in STATUS_COLORS) return STATUS_COLORS[raw]
  return { label: raw ? raw[0].toUpperCase() + raw.slice(1) : "Unknown", hex: "#6B7280" }
}

// ——— Main PDF generator ————————————————————————————————————————————————

export default async function generateTransactionsPDF(
  title: string,
  transactions: any[],
  type: "user" | "client",
  user: { firstName: string; lastName: string; user_id: string }
) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm" })
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const brand = "#5B6EF5"
  const brandRgb = hexToRgbArray(brand)
  const dateStr = dayjs().format("M/D/YYYY")

  const logoBase64 = await svgToPngBase64(renderToStaticMarkup(<SvgZoeeGradientLogo />))

  // — Header bar —
  doc.setFillColor(brand)
  doc.rect(0, 0, pageWidth, 20, "F")

  doc.setFont("helvetica", "bold")
  doc.setFontSize(16)
  doc.setTextColor("#FFFFFF")
  doc.text(title, pageWidth / 2, 13, { align: "center" })

  // — User info (no "User:" label) —
  const userName = `${user.firstName || ""} ${user.lastName || ""}`.trim() || "—"
  const userId = user.user_id || "—"

  doc.setFont("helvetica", "normal")
  doc.setFontSize(10)
  doc.setTextColor("#444444")
  doc.text(userName, 10, 30)
  doc.text(`User ID: ${userId}`, 10, 36)

  doc.setTextColor("#555555")
  doc.text(`Generated on ${dateStr}`, pageWidth - 10, 30, { align: "right" })

  // — Normalize table rows —
  const normalized = transactions.map((t) => {
    const amount = t.amount !== null && t.amount !== undefined ? Number(t.amount) : null
    const status = computeStatus(t)
    const paymentMethod = formatPaymentMethod(t.payment_method ?? t.paymentMethod, amount)

    return {
      dateProcessed: t.date_processed ? dayjs(t.date_processed).format("M/D/YYYY") : "—",
      dateScheduled:
        !t.date_processed && t.date_scheduled
          ? dayjs(t.date_scheduled).format("M/D/YYYY")
          : "—",
      clientName: t.client_name || t.client || "Unknown Client",
      serviceName: t.service_name || t.service || t.description || "Service",
      promoCode: t.promo_code || t.promo || "—",
      paymentMethod,
      statusLabel: status.label,
      statusHex: status.hex,
      amount: formatMoney(amount),
      amountNet: formatMoney(t.amount_net),
      totalFees: formatMoney(t.total_fee_amount),
      appFee: formatMoney(t.application_fee_amount),
      stripeMerchantFee: formatMoney(t.stripe_merchant_fee_amount),
      stripeTransactionFee: formatMoney(t.stripe_transaction_fee_amount),
    }
  })

  // — Column headers (always 13 columns) —
  const HEAD = [
    [
      "Processed",
      "Scheduled",
      "Client",
      "Service",
      "Promo Code",
      "Payment Method",
      "Status",
      "Amount",
      "Net",
      "Total Fees",
      "App Fee",
      "Merchant Fee",
      "Txn Fee",
    ],
  ]

  const BODY: RowInput[] = normalized.map((r) => [
    r.dateProcessed,
    r.dateScheduled,
    r.clientName,
    r.serviceName,
    r.promoCode,
    r.paymentMethod,
    r.statusLabel,
    r.amount,
    r.amountNet,
    r.totalFees,
    r.appFee,
    r.stripeMerchantFee,
    r.stripeTransactionFee,
  ])

  // — Table —
  autoTable(doc, {
    startY: 42,
    head: HEAD,
    body: BODY,
    styles: {
      font: "helvetica",
      fontSize: 8.5,
      cellPadding: { top: 2.2, right: 2.2, bottom: 2.2, left: 2.2 },
      textColor: [51, 51, 51],
      lineColor: [235, 235, 235],
      lineWidth: 0.2,
      valign: "middle",
    },
    headStyles: {
      fillColor: brandRgb,
      textColor: [255, 255, 255],
      fontStyle: "bold",
      halign: "center",
    },
    alternateRowStyles: { fillColor: [250, 250, 255] },
    columnStyles: {
      7: { halign: "right" },
      8: { halign: "right" },
      9: { halign: "right" },
      10: { halign: "right" },
      11: { halign: "right" },
      12: { halign: "right" },
      0: { halign: "center" },
      1: { halign: "center" },
      6: { halign: "center" },
      5: { halign: "center" },
      4: { halign: "center" },
    },
    didParseCell: (data) => {
      // color status column text
      if (data.section === "body" && data.column.index === 6) {
        const rowIdx = data.row.index
        const hex = normalized[rowIdx].statusHex || "#6B7280"
        const rgb = hexToRgbArray(hex)
        data.cell.styles.textColor = rgb
        data.cell.styles.fontStyle = "bold"
      }

      // dim promo code if dash
      if (data.section === "body" && data.column.index === 4) {
        if (String(data.cell.raw).trim() === "—") {
          data.cell.styles.textColor = [150, 150, 150]
        }
      }
    },
    didDrawPage: () => {
      // footer: centered "Powered by Zoee" + logo
      const poweredBy = "Powered by"
      const y = pageHeight - 8

      doc.setFont("helvetica", "normal")
      doc.setFontSize(8)
      doc.setTextColor("#9CA3AF")

      const textWidth = doc.getTextWidth(poweredBy)
      const logoW = 18
      const spacing = 2
      const totalW = textWidth + spacing + logoW
      const startX = (pageWidth - totalW) / 2

      doc.text(poweredBy, startX, y)
      doc.addImage(logoBase64, "PNG", startX + textWidth + spacing, y - 6.5, logoW, 7)

      const pageNo = String(doc.getCurrentPageInfo().pageNumber)
      doc.setFontSize(8)
      doc.setTextColor("#A3A3A3")
      doc.text(`Page ${pageNo}`, pageWidth - 8, pageHeight - 6, { align: "right" })
    },
    margin: { top: 42, right: 8, bottom: 16, left: 8 },
    tableWidth: "auto",
    pageBreak: "auto",
  })

  doc.save(`${title.replace(/\s+/g, "_")}_${dayjs().format("YYYY-MM-DD")}.pdf`)
}